import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { useStore } from '../../../components/store/Store';
import { useControlledFieldArray } from '../../../hooks/useControlledFieldArray';
import Container from '../../../components/Container';
import { Page } from '../../../declarations/models/Page';
import { Site } from '../../../declarations/models/Site';
import TextInput from '../../../components/forms/TextInput';
import { SectionsPath } from '../../../editor/PageEditor/CurrentSectionProvider';
import {
  getOrCreateSettingsContentSectionBlockIndex,
  getOrCreateSettingsContentSectionIndex,
} from '../../../utils/SettingsContent';
import { SectionType } from '../../../declarations/models/SectionType';
import CollapseContainer from '../../../components/CollapseContainer';
import { BlockType } from '../../../declarations/models/BlockType';
import { BlockPath } from '../../../editor/PageEditor/CurrentBlockProvider';
import LanguageFooterAddressSettings from './LanguageFooterAddressSettings';
import LanguageFooterSocialSettings from './LanguageFooterSocialSettings';
import LanguageFooterMenuSettings from './LanguageFooterMenuSettings';

export const LanguageFooterSettings: FC = () => {
  const { t } = useTranslation('components');
  const { fields: sections, append } = useControlledFieldArray<Page, SectionsPath>({ name: 'content.sections' });
  const siteFooterSectionIndex = getOrCreateSettingsContentSectionIndex(SectionType.SITE_FOOTER, append, sections);
  const { state } = useStore();
  const isSuperAdmin = state.currentUser?.user?.is_super_admin;

  const footerAddressBlockPath =
    `content.sections.${siteFooterSectionIndex}.blocks.${getOrCreateSettingsContentSectionBlockIndex(
      siteFooterSectionIndex,
      BlockType.FOOTER_ADDRESS,
      sections,
    )}` as BlockPath;

  const footerSocialBlockPath =
    `content.sections.${siteFooterSectionIndex}.blocks.${getOrCreateSettingsContentSectionBlockIndex(
      siteFooterSectionIndex,
      BlockType.FOOTER_SOCIAL,
      sections,
    )}` as BlockPath;

  const footerMenuBlockPath =
    `content.sections.${siteFooterSectionIndex}.blocks.${getOrCreateSettingsContentSectionBlockIndex(
      siteFooterSectionIndex,
      BlockType.FOOTER_MENU,
      sections,
    )}` as BlockPath;

  const footerMenuValue = useWatch({ name: footerMenuBlockPath });

  const menuCount = footerMenuValue?.items?.length || 0;

  const htmlFooter = useWatch<Site, `content.settings.htmlFooter`>({
    name: `content.settings.htmlFooter`,
    defaultValue: '',
  });

  return (
    <Container column fullWidth>
      <CollapseContainer title={t('Settings.LanguageSettings.LanguageFooterSettings.addressTitle')}>
        <LanguageFooterAddressSettings blockPath={footerAddressBlockPath} />
      </CollapseContainer>
      <CollapseContainer title={t('Settings.LanguageSettings.LanguageFooterSettings.socialTitle')}>
        <LanguageFooterSocialSettings blockPath={footerSocialBlockPath} />
      </CollapseContainer>
      <CollapseContainer
        title={t('Settings.titles.footerMenu')}
        subTitle={t('Settings.LanguageSettings.childCount', { count: menuCount })}>
        <LanguageFooterMenuSettings blockPath={footerMenuBlockPath} />
      </CollapseContainer>

      {isSuperAdmin && (
        <div className='superadmin' style={{ backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
          <CollapseContainer title='HTML Footer'>
            <Container fullWidth column left gap={2}>
              <p>
                Brukes i noen svært få tilfelller som et komplett alternativ til øvrige footerinnstillinger. Merk at om
                noe angis er det kun disse dataenes som vil vises i footer.
              </p>
              <Container fullWidth column gap={2}>
                <TextInput
                  path='content.settings.htmlFooter'
                  label='HTML kode (flyttes hit fra 1.0 Widget)'
                  multiline
                  minRows={7}
                />
              </Container>
            </Container>
          </CollapseContainer>
        </div>
      )}
    </Container>
  );
};

export default LanguageFooterSettings;
